import { useState, useEffect } from "react";
import { Wrench, Menu, X, Zap, Sun, Moon, LogIn, LayoutDashboard, LogOut } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Diagnosis", href: "#diagnosis" },
  { label: "Tracking", href: "#tracking" },
  { label: "FAQ", href: "#faq" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (href) => {
    setOpen(false);
    if (!onLanding) {
      navigate("/", { state: { scrollTo: href } });
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const goHome = () => {
    setOpen(false);
    if (onLanding) window.scrollTo({ top: 0, behavior: "smooth" });
    else navigate("/");
  };

  const isDark = theme === "dark";

  const headerBg = scrolled
    ? isDark
      ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
      : "bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-sm"
    : "bg-transparent";

  const linkColor = isDark
    ? "text-[#8F9098] hover:text-white"
    : "text-slate-500 hover:text-brand-blue font-medium";

  const brandText = isDark ? "text-white" : "text-slate-900";
  const toggleBtnBase = isDark
    ? "border-white/10 hover:border-[#00F0FF]/40 text-white/80"
    : "border-slate-200 hover:border-brand-blue/40 text-slate-500 hover:text-brand-blue";

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 h-16 flex items-center justify-between">
        {/* Logo */}
        <button onClick={goHome} className="flex items-center gap-2 group shrink-0 mr-6">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${isDark ? "bg-[#00F0FF]/10 border border-[#00F0FF]/30 group-hover:bg-[#00F0FF]/20" : "bg-gradient-to-br from-brand-blue to-brand-blue-light shadow-md"}`}>
            <Wrench size={16} className={isDark ? "text-[#00F0FF]" : "text-white"} />
          </div>
          <span className={`font-display font-bold tracking-tight text-lg ${brandText}`}>
            Wrench<span className={isDark ? "text-[#00F0FF]" : "text-brand-orange"}>OnWheels</span>
          </span>
        </button>

        {/* Desktop Links */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => goSection(l.href)}
              className={`text-sm transition-colors tracking-wide ${linkColor}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            className={`haptic w-9 h-9 inline-flex items-center justify-center rounded-md border transition-all duration-200 ${toggleBtnBase}`}
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {user ? (
            <>
              <button
                onClick={() => navigate("/dashboard")}
                className={`haptic flex items-center gap-2 px-3 py-2 rounded-md text-sm border transition-colors ${toggleBtnBase}`}
              >
                <LayoutDashboard size={14} />
                Dashboard
              </button>
              <button
                onClick={() => { logout(); navigate("/"); }}
                className={`haptic flex items-center gap-2 px-3 py-2 rounded-md text-sm border transition-colors ${toggleBtnBase}`}
              >
                <LogOut size={14} />
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className={`haptic flex items-center gap-2 px-3 py-2 rounded-md text-sm border transition-colors ${toggleBtnBase}`}
            >
              <LogIn size={14} />
              Login
            </button>
          )}

          <button
            data-testid="book-now-button"
            onClick={() => goSection("#booking")}
            className={`btn-magnetic haptic flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm transition-all duration-200 ${
              isDark
                ? "bg-[#00F0FF] text-black shadow-[0_0_20px_rgba(0,240,255,0.4)]"
                : "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-[0_4px_16px_rgba(249,115,22,0.35)] hover:shadow-[0_6px_24px_rgba(249,115,22,0.45)]"
            }`}
          >
            <Zap size={14} />
            Book Now
          </button>
        </div>

        {/* Mobile actions */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            className={`w-10 h-10 inline-flex items-center justify-center rounded-lg border transition-colors ${toggleBtnBase}`}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            className={`w-10 h-10 inline-flex items-center justify-center rounded-lg transition-colors ${isDark ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"}`}
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          isDark
            ? "bg-black/95 border-b border-white/10"
            : "bg-white/98 border-b border-slate-200"
        } backdrop-blur-xl ${open ? "max-h-[32rem] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <nav className="px-6 py-3 flex flex-col">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => goSection(l.href)}
              className={`text-left py-4 text-base font-medium transition-colors border-b last:border-0 ${
                isDark
                  ? "text-[#8F9098] hover:text-white border-white/5"
                  : "text-slate-600 hover:text-brand-blue border-slate-100"
              }`}
            >
              {l.label}
            </button>
          ))}
          {user ? (
            <>
              <button
                onClick={() => { setOpen(false); navigate("/dashboard"); }}
                className={`text-left py-4 text-base font-medium ${isDark ? "text-white" : "text-slate-900"}`}
              >
                Dashboard
              </button>
              <button
                onClick={() => { setOpen(false); logout(); navigate("/"); }}
                className={`text-left py-4 text-base font-medium ${isDark ? "text-white" : "text-slate-900"}`}
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={() => { setOpen(false); navigate("/login"); }}
              className={`text-left py-4 text-base font-medium ${isDark ? "text-white" : "text-brand-blue"}`}
            >
              Login / Sign up
            </button>
          )}
          <button
            data-testid="book-now-button-mobile"
            onClick={() => goSection("#booking")}
            className={`btn-magnetic haptic mt-4 w-full py-4 rounded-xl font-bold text-base ${
              isDark
                ? "bg-[#00F0FF] text-black"
                : "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-lg"
            }`}
          >
            Book Now
          </button>
        </nav>
      </div>
    </header>
  );
}
